import { motion } from 'framer-motion';
import { CheckCircle2, Pencil, Wallet } from 'lucide-react';
import { useMemo } from 'react';
import {
  BottomSheet,
  Button,
  NumPad,
  ProgressBar,
} from '../../components';
import type { Category, Transaction } from '../../data/types';
import { effectiveSpreadEnd } from '../../lib/budget';
import { cn } from '../../lib/cn';
import { formatCurrency } from '../../lib/currency';
import {
  addDays,
  diffInDays,
  fromIsoDate,
  startOfDay,
  toIsoDate,
} from '../../lib/date';
import { resolveIcon } from '../../lib/icons';
import { springQuick, tapScale } from '../../tokens';

type Props = {
  /** Null while closed; the spread transaction being managed when open. */
  transaction: Transaction | null;
  category: Category | undefined;
  currency: string;
  today: Date;
  onClose: () => void;
  /** Apply changes to the transaction (paid off / partial payment). */
  onPatch: (id: string, patch: Partial<Transaction>) => void;
  /** Open the full edit flow for this transaction. */
  onEdit: (tx: Transaction) => void;
};

/**
 * Focused management sheet for a spread transaction. Shows the
 * progress bar (days elapsed / total), days remaining + remaining
 * dollars, and three actions:
 *   - Mark paid off — sets spreadEndedOn to today, forgiving the
 *     entire remaining future contribution.
 *   - Apply payment — opens an inline NumPad; the entered amount is
 *     converted to days at the per-day rate and the effective end
 *     shifts that many days earlier (compounds across multiple
 *     payments without tracking total paid).
 *   - Edit details — closes this sheet and opens EditTransactionSheet.
 */
export function SpreadManageSheet({
  transaction,
  category,
  currency,
  today,
  onClose,
  onPatch,
  onEdit,
}: Props) {
  const open = !!transaction;
  const Icon = category ? resolveIcon(category.icon) : Wallet;

  const stats = useMemo(() => {
    if (!transaction) return null;
    const days =
      transaction.spreadDays && transaction.spreadDays > 1
        ? transaction.spreadDays
        : 1;
    const start = startOfDay(fromIsoDate(transaction.date));
    const naturalEnd = addDays(start, days - 1);
    const effEnd = effectiveSpreadEnd(transaction);
    const t0 = startOfDay(today);
    // Use the user's intended spread length for the denominator.
    // diffInDays(start, effEnd) + 1 collapses to 0 when spreadEndedOn
    // lands before tx.date (applyPayment edge case, stale data),
    // producing 'Day 1 of 0' nonsense.
    // NOTE: diffInDays(a, b) returns b - a, so the EARLIER date is the
    // first arg when you want a positive count.
    const totalDays = days;
    const elapsed = Math.min(
      totalDays,
      Math.max(0, diffInDays(start, t0) + 1),
    );
    const remainingDays = Math.max(0, diffInDays(t0, effEnd));
    const dailyRate = days > 0 ? transaction.amount / days : 0;
    const remainingFutureCost = dailyRate * remainingDays;
    const isShortened = effEnd.getTime() < naturalEnd.getTime();
    return {
      days,
      totalDays,
      isShortened,
      elapsed,
      remainingDays,
      dailyRate,
      remainingFutureCost,
      naturalEndIso: toIsoDate(naturalEnd),
      effectiveEndIso: toIsoDate(effEnd),
      progress: totalDays > 0 ? Math.min(1, elapsed / totalDays) : 1,
    };
  }, [transaction, today]);

  const markPaidOff = () => {
    if (!transaction) return;
    onPatch(transaction.id, { spreadEndedOn: toIsoDate(startOfDay(today)) });
    onClose();
  };

  const applyPayment = (paymentAmount: number) => {
    if (!transaction || !stats || paymentAmount <= 0) return;
    if (stats.dailyRate <= 0) return;

    // Convert the payment to days saved at the per-day rate. Floor so
    // partial days don't over-credit. Compound against the current
    // effective end so multiple payments shorten further.
    const daysSaved = Math.floor(paymentAmount / stats.dailyRate);
    if (daysSaved <= 0) {
      // Payment too small to retire a full day; treat as paid through
      // today (closer than nothing).
      onPatch(transaction.id, {
        spreadEndedOn: toIsoDate(startOfDay(today)),
      });
      onClose();
      return;
    }
    const currentEnd = startOfDay(fromIsoDate(stats.effectiveEndIso));
    const shifted = addDays(currentEnd, -daysSaved);
    const t0 = startOfDay(today);
    const newEnd = shifted.getTime() < t0.getTime() ? t0 : shifted;
    onPatch(transaction.id, { spreadEndedOn: toIsoDate(newEnd) });
    onClose();
  };

  return (
    <BottomSheet open={open} onClose={onClose} title="Manage spread">
      {transaction && stats ? (
        <>
          <div className="px-card-pad pb-2 pt-1 flex items-center gap-3">
            <div
              className="w-10 h-10 rounded-button shrink-0 inline-flex items-center justify-center text-white shadow-sm"
              style={{ backgroundColor: category?.color ?? '#8E8E93' }}
            >
              <Icon size={18} strokeWidth={1.5} />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-[15px] font-semibold text-text-primary truncate">
                {transaction.description?.trim() ||
                  category?.name ||
                  'Spread purchase'}
              </div>
              <div className="text-[12px] text-text-secondary mt-0.5">
                {formatCurrency(transaction.amount, { currency })} ·{' '}
                {formatCurrency(stats.dailyRate, { currency })}/day
              </div>
            </div>
          </div>

          {/* Progress + remaining */}
          <div className="px-card-pad pb-3">
            <ProgressBar value={stats.progress} />
            <div className="mt-2 flex items-baseline justify-between text-[12px]">
              <span className="text-text-tertiary">
                Day {stats.elapsed} of {stats.totalDays}
                {stats.isShortened ? (
                  <span className="text-text-tertiary">
                    {' '}
                    (ended early)
                  </span>
                ) : null}
              </span>
              <span className="font-mono tabular-nums text-text-secondary">
                {formatCurrency(stats.remainingFutureCost, { currency })} over{' '}
                {stats.remainingDays} more day
                {stats.remainingDays === 1 ? '' : 's'}
              </span>
            </div>
          </div>

          {/* Actions — copy + buttons branch on direction. */}
          <div className="px-card-pad flex flex-col gap-2">
            <Button
              variant="primary"
              size="lg"
              fullWidth
              leadingIcon={<CheckCircle2 size={18} strokeWidth={1.5} />}
              onClick={markPaidOff}
            >
              {transaction.type === 'income'
                ? 'End spread now'
                : 'Mark paid off'}
            </Button>
            <p className="text-[11px] text-text-tertiary text-center -mt-1">
              {transaction.type === 'income' ? (
                <>
                  Stops counting{' '}
                  <span className="font-mono">
                    {formatCurrency(stats.remainingFutureCost, { currency })}
                  </span>{' '}
                  toward the daily budget for the remaining days.
                </>
              ) : (
                <>
                  Forgives the remaining{' '}
                  <span className="font-mono">
                    {formatCurrency(stats.remainingFutureCost, { currency })}
                  </span>{' '}
                  from future budgets.
                </>
              )}
            </p>

            <motion.button
              type="button"
              onClick={() => onEdit(transaction)}
              whileTap={tapScale}
              transition={springQuick}
              className={cn(
                'w-full inline-flex items-center justify-center gap-2',
                'h-11 rounded-button bg-surface-secondary text-text-primary',
                'text-[14px] font-medium',
              )}
            >
              <Pencil size={16} strokeWidth={1.5} />
              Edit details
            </motion.button>
          </div>

          {/* Apply payment NumPad — only meaningful for expense spreads.
              For income, partial payments don't make conceptual sense
              (you don't pay yourself extra to shorten an income window).
              Edit + End spread cover the relevant cases. */}
          {transaction.type === 'expense' ? (
            <div className="mt-3">
              <div className="h-px bg-surface-secondary mx-card-pad" />
              <NumPad
                key={`payment-${transaction.id}`}
                initialValue={0}
                onDone={applyPayment}
                doneLabel="Apply payment"
                caption="Made a payment of"
              />
            </div>
          ) : null}
        </>
      ) : null}
    </BottomSheet>
  );
}
